<template>
  <div v-if="loading" class="text-center">
      <b-spinner
        small
        variant="primary"
        type="grow"
        label="Spinning"/>
      <b-spinner
        small
        variant="primary"
        type="grow"
        label="Spinning"/>
      <b-spinner
        small
        variant="primary"
        type="grow"
        label="Spinning"/>
  </div>
  <div v-else>
    <b-button
      v-b-toggle="'list-domains'"
      class="m-1"
      variant="info">
          <span class="when-open"><font-awesome-icon :icon="['fas', 'chevron-up']" />&nbsp;</span>
          <span class="when-closed"><font-awesome-icon :icon="['fas', 'chevron-down']" />&nbsp;</span>
          {{$t('label.list_domains')}}
      </b-button>
    <b-collapse id="list-domains">
      <b-alert variant=warning :show="establishment.esta_domain_only">L'établissement a été configuré pour utiliser que ces domaines d'activités et non ceux par défaut.</b-alert>
      <b-container>
        <b-row class="justify-content-md-center">
          <b-col>
            <b-input-group>
              <b-form-input type="text" v-model="search" :placeholder="$t('label.search')"></b-form-input>
              <b-input-group-append>
                <b-button variant="secondary" :disabled="!search" @click="search = ''">{{ $t('btn.erase') }}</b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col><b-button variant="primary" @click="showSetDomainModal">{{ $t('btn.add_domain') }}</b-button></b-col>
          <b-col><b-button variant="secondary" @click="showCreateTemplateModal">{{ $t('btn.add_template_domain') }}</b-button></b-col>
          <b-col><b-button variant="warning" @click="showApplyTemplateModal" :disabled="establishment.esta_domain_only">{{ $t('btn.apply_template_domain') }}</b-button></b-col>
        </b-row>
      </b-container>
      <b-table
        :fields="fields"
        :items="searchDomains">

        <template #cell(dac_label)="row">
          <span :style="'text-decoration: underline;text-decoration-thickness: 2px;text-decoration-color: ' + row.item?.dac_data?.display?.color">{{ row.item.dac_label }}</span>
        </template>
        <template #cell(dac_is_enable)="row">
          <b-badge :variant="row.item.dac_is_enable ? 'success' : 'danger'" >
            {{ row.item.dac_is_enable ? 'Actif' : 'Inactif' }}
          </b-badge>
        </template>
        <template #cell(override)="row">
          <b-badge :variant="row.item.override ? 'success' : 'secondary'" >
            {{ row.item.override ? 'Oui' : 'Non' }}
          </b-badge>
        </template>
        <template #cell(dac_last_modified)="row">
          {{ formatDate(row.item.dac_last_modified) }}
        </template>
        <template #cell(patgamesActivities)="row">
          <div
            :class="getActivitiesFromDomain(row.item.dac_id, 'patgames').length > 0 ? 'link' : ''"
            @click="getActivitiesFromDomain(row.item.dac_id, 'patgames').length > 0 ? moveActivitiesFromDomain(row.item, false) : null">
            {{ getActivitiesFromDomain(row.item.dac_id, 'patgames').length }}
          </div>
        </template>
        <template #cell(externalActivities)="row">
          <div
            :class="getActivitiesFromDomain(row.item.dac_id, 'external').length > 0 ? 'link' : ''"
            @click="getActivitiesFromDomain(row.item.dac_id, 'external').length > 0 ? moveActivitiesFromDomain(row.item, false) : null">
            {{ getActivitiesFromDomain(row.item.dac_id, 'external').length }}
          </div>
        </template>

        <template #cell(actions)="row">
          <b-button
            size="sm"
            variant="primary"
            :disabled="updateLoading"
            @click="showSetDomainModal(row.item)"
          >
            {{ $t("btn.update") }}
          </b-button>
        </template>
      </b-table>
    </b-collapse>

    <modal-set-domain ref="modalSetDomain" :domains="domains" :loading="updateLoading" @onSubmit="submitDomain" />
    <modal-create-template ref="modalCreateTemplate" :domains="domains" :patgames="patgamesActivities" :templates="templates" :establishment="establishment" />
    <modal-move-activities-from-domain ref="modalMoveActivitiesFromDomain" :establishment="establishment" :domains="domains" @onMoved="onActivitiesMoved" />
    <modal-apply-domain-template ref="modalApplyDomainTemplate" :establishment="establishment" :templates="templates" @onApplied="loadDomainsAndActivities"/>
  </div>
</template> 

<script>
import ModalCreateTemplate from "../ModalCreateTemplate.vue";
import ModalSetDomain from "../ModalSetDomain.vue";
import ModalMoveActivitiesFromDomain from "../ModalMoveActivitesFromDomain.vue";
import ModalApplyDomainTemplate from "../ModalApplyDomainTemplate.vue";

import { getDomains, setDomain, getActivitesFromDomain, getTemplatesDomain } from "../../services/establishment"
import { GetAllCustomActivities } from "../../services/customactivities"
import { getPatgames } from "../../services/patgames";

import { normalizeString } from "../../misc/utils"

export default {
  name: "ListDomains",
  props: {
    establishment: {
      type: Object,
      required: true,
    }
  },
  components: {
    ModalSetDomain,
    ModalCreateTemplate,
    ModalMoveActivitiesFromDomain,
    ModalApplyDomainTemplate,
  },
  data() {
    return {
      loading: false,
      updateLoading: false,
      search: '',
      domains: [],
      templates: [],
      patgamesActivities: [],
      externalActivities: [],
      pendingDomainUpdate: null,
    };
  },
  computed: {
    searchDomains() {
      return this.domains.filter((domain) => {
        return normalizeString(domain.dac_label).includes(normalizeString(this.search));
      });
    },
    fields() {
      return [ 
        {
          dac_label: {
            label: this.$t('label.domain_name'),
            sortable: true,
          }
        },
        {
          dac_is_enable: {
            label: this.$t('label.domain_state'),
            sortable: false,
          }
        },
        {
          override: {
            label: this.$t('label.domain_is_override'),
            sortable: false,
          }
        },
        {
          dac_last_modified: {
            label: this.$t('label.domain_last_updated'),
            sortable: true,
          }
        },
         {
          patgamesActivities: {
            label: this.$t('label.patgames_activities'),
            sortable: false,
          },
        },
        {
          externalActivities: {
            label: this.$t('label.external_activities'),
            sortable: false,
          },
        },
        {
          actions: {
            label: this.$t('label.action'),
            sortable: false,
          }
        },
      ];
    }
  },
  async created() {
    this.loading = true;
    [this.domains, this.templates, this.externalActivities, this.patgamesActivities] = await Promise.all([
      getDomains(this.establishment.esta_id),
      getTemplatesDomain(),
      GetAllCustomActivities(this.establishment.esta_id, true),
      getPatgames(this.establishment.esta_id),
    ]);
    this.loading = false;
  },
  methods: {
    async loadDomains() {
      this.domains = await getDomains(this.establishment.esta_id);
    },
    async loadActivities() {
      [this.externalActivities, this.patgamesActivities] = await Promise.all([
        GetAllCustomActivities(this.establishment.esta_id, true),
        getPatgames(this.establishment.esta_id)
      ]);
    },
    async loadDomainsAndActivities() {
      await Promise.all([this.loadDomains(), this.loadActivities()]);
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString();
    },
    showSetDomainModal(domain = null) {
      this.pendingDomainUpdate = null;
      this.$refs.modalSetDomain.show(domain);
    },
    showCreateTemplateModal() {
      this.$refs.modalCreateTemplate.show();
    },
    showApplyTemplateModal() {
      this.$refs.modalApplyDomainTemplate.show();
    },
    async moveActivitiesFromDomain(domain, disable=true) {
      const activities = await getActivitesFromDomain(this.establishment.esta_id, domain.dac_id);
        if (activities.length > 0) {
          this.$refs.modalMoveActivitiesFromDomain.show(domain, activities, disable);
          return true;
        }
        return false;
    },
    async submitDomain(newDomain) {
      if (newDomain.dac_id) {
        const originalDomain = this.domains.find(d => d.dac_id === newDomain.dac_id);

        if (originalDomain?.dac_is_enable && !newDomain.dac_is_enable) {
          const activitiesMoved = await this.moveActivitiesFromDomain(originalDomain);
          if (activitiesMoved) {
            this.pendingDomainUpdate = newDomain;
            this.$refs.modalSetDomain.hide();
            return;
          }
        }
      }
      await this.saveDomain(newDomain);
    },
    async saveDomain({ dac_id, dac_label, dac_data, dac_is_enable }) {
      this.updateLoading = true;
      try {
        const updatedDomain = await setDomain(
          this.establishment.esta_id,
          dac_id ?? null,
          dac_label,
          dac_data.display.color,
          dac_is_enable
        );

        if (dac_id) {
          const index = this.domains.findIndex(item => item.dac_id === dac_id);
          this.$set(this.domains, index, {...this.domains[index], ...updatedDomain});
        }
        else {
          this.domains.push({...updatedDomain});
        }

        this.updateLoading = false;
        this.$refs.modalSetDomain.hide();
        this.showToast(
          'success',
          this.$t(dac_id ? 'label.domain_updated_success' : 'label.domain_added_success'),
          this.$t(dac_id ? 'label.domain' : 'label.domain_add')
        );
      } catch {
        this.updateLoading = false;
        this.showToast('danger', this.$t('error.general'), this.$t('label.domain'));
      }
    },
    async onActivitiesMoved(domain) {
      if (domain !== null && this.pendingDomainUpdate.dac_id === domain.dac_id) {
        const update = this.pendingDomainUpdate;
        this.pendingDomainUpdate = null;
        await this.saveDomain(update);
      }
      await this.loadActivities();
    },
    getActivitiesFromDomain(dacid, type='patgames') {
      if (type === 'patgames') {
        return this.patgamesActivities.filter(pat => pat.act_dac_id === dacid);
      } else {
        return this.externalActivities.filter(ea => ea.dac_id === dacid);
      }
    }
  },
}
</script>

<style scoped>
.link {
  cursor: pointer;
  text-decoration: underline;
  color: #007bff;
}
</style>
